import React, { useContext, useEffect, useRef } from 'react';
import { UiContext } from 'shared/lib/contexts/uiContext';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { Box, IconButton, Paper, Typography, useTheme } from '@mui/material';
import map from 'lodash-es/map';
import LinearProgressWithLabel from 'shared/lib/components/progresses/linearProgressWithLabel';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import some from 'lodash-es/some';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import {
  UploadWatcher,
  UploadStatus,
} from 'shared/lib/extendedComponents/uploadWatcher/uploadWatcher';
import { observer } from 'mobx-react-lite';
import IconRetry from 'shared/icons/iconRetry';
import IconOk from 'shared/icons/iconOk';
import IconStop from 'shared/icons/iconStop';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import each from 'lodash-es/each';

export type ProgressModalItem = {
  percent: number;
  name: string;
  abort: () => void;
};

export default observer(function WatchUploadProgressModal({
  items,
  handleClose,
  headerText,
  navigateToUploaded,
  ...other
}: {
  items: UploadWatcher[];
  navigateToUploaded: () => void;
  headerText: string;
} & Omit<ModalProps, 'children'>) {
  const theme = useTheme();
  const {
    spacings: { sp, icons },
  } = useContext(UiContext);

  const isSomePending = some(
    items,
    (item) => item.status === UploadStatus.PENDING
  );
  const isSomeSuccess = some(
    items,
    (item) => item.status === UploadStatus.SUCCESS
  );

  const interval = useRef<NodeJS.Timer | null>(null);
  const clearItemsRef = useRef<UploadWatcher[] | null>(null);

  const clear = useRef(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }
    if (clearItemsRef.current) {
      each(clearItemsRef.current, (f) => f.abort());
    }
  });

  useEffect(() => {
    clear.current();

    const maxPendingCount = 3;
    interval.current = setInterval(() => {
      let pendingCount = 0;

      for (let i = 0; i < items.length; i++) {
        if (pendingCount === maxPendingCount) {
          return;
        }

        const f = items[i];

        if (f.status === UploadStatus.PENDING) {
          pendingCount += 1;
        } else if (f.status === UploadStatus.INITIAL) {
          f.startUpload();
          pendingCount += 1;
        }
      }
    }, 1000);

    clearItemsRef.current = items;
  }, [items]);

  useEffect(() => {
    return () => clear.current();
  }, []);

  return (
    <Modal
      {...other}
      disableCloseIcon={true}
      sx={{
        '& .MuiDialog-paper': {
          width: 0.8,
          minWidth: 320,
          pt: sp.s80,
          px: 0,
          pb: 0,
          overflow: 'hidden',
        },
      }}
      handleClose={handleClose}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 1,
          overflow: 'hidden',
        }}
      >
        <Typography variant="f24" sx={{ px: sp.s80, pb: sp.s80 }}>
          {headerText}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: sp.s40,
            px: sp.s80,
            pb: sp.s80,
            overflowY: 'auto',
            flexGrow: 1,
          }}
        >
          {map(items, (item, i) => {
            return (
              <Box
                key={i}
                sx={{ display: 'flex', flexDirection: 'column', width: 1 }}
              >
                <Typography variant="f14">{item.name}</Typography>
                <Box
                  sx={{ display: 'flex', gap: sp.s20, alignItems: 'center' }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgressWithLabel
                      color={
                        item.status === UploadStatus.ABORTED
                          ? 'warning'
                          : item.status === UploadStatus.SUCCESS
                          ? 'success'
                          : item.status === UploadStatus.FAIL
                          ? 'error'
                          : 'primary'
                      }
                      value={item.progressAsPercent}
                    ></LinearProgressWithLabel>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: icons['24x24'].w,
                      height: icons['24x24'].h,
                    }}
                  >
                    {item.status === UploadStatus.ABORTED ? (
                      <IconButton onClick={() => item.startUpload()}>
                        <IconRetry
                          sizeKey="16x16"
                          extColor={theme.palette.primary.main}
                        ></IconRetry>
                      </IconButton>
                    ) : item.status === UploadStatus.SUCCESS ? (
                      <IconOk
                        sizeKey="24x24"
                        extColor={theme.palette.success.main}
                      ></IconOk>
                    ) : item.status === UploadStatus.FAIL ? (
                      <IconButton onClick={() => item.startUpload()}>
                        <IconRetry
                          sizeKey="16x16"
                          extColor={theme.palette.error.main}
                        ></IconRetry>
                      </IconButton>
                    ) : item.status === UploadStatus.PENDING ? (
                      <IconButton onClick={() => item.abort()}>
                        <IconStop
                          sizeKey="16x16"
                          extColor={theme.palette.error.main}
                        ></IconStop>
                      </IconButton>
                    ) : (
                      ''
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Paper
          square
          sx={{
            py: sp.s50,
            px: sp.s80,
            borderTop: 1,
            borderColor: 'custom.lightGrey',
          }}
        >
          {isSomePending ? (
            <ButtonsContainer>
              <PrimaryButton onClick={clear.current}>Прервать</PrimaryButton>
            </ButtonsContainer>
          ) : (
            <ButtonsContainer>
              <SecondaryButton onClick={handleClose}>Закрыть</SecondaryButton>

              {isSomeSuccess ? (
                <PrimaryButton
                  onClick={() => {
                    navigateToUploaded();
                  }}
                >
                  Перейти к файлам
                </PrimaryButton>
              ) : (
                ''
              )}
            </ButtonsContainer>
          )}
        </Paper>
      </Box>
    </Modal>
  );
});
